import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { cmsApiConfig } from '../../cmsConfig';
import { ContactAttempt } from '../../types/ContactAttempts/ContactAttempt';
import { ContactAttemptTypeText } from '../../types/Enums/ContactAttemptType';
import { ContactAttemptAnswerText } from '../../types/Enums/ContactAttemptAnswer';
import { UpdateContactAttempt } from '../../types/ContactAttempts/UpdateContatAttempt';
import { AddContactAttemptResponse } from '../../types/ContactAttempts/AddContactAttemptResponse';
import { AddContactAttempt } from '../../types/ContactAttempts/AddContactAttempt';
import { ContactAttemptsTexts } from '../../types/ContactAttempts/ContactAttemptsTexts';
import { PortalTypes } from '../../types/Enums/PortalTypes';

export const contactAttemptsApi = createApi({
  reducerPath: 'contactAttemptsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${cmsApiConfig.url}/portalapi/contactAttempts`,
    headers: {
      'X-CSRF': '1',
    },
    credentials: 'include',
  }),
  tagTypes: ['ContactAttempts', 'PageTexts'],
  endpoints: (builder) => ({
    getPageTexts: builder.query<ContactAttemptsTexts, void>({
      query: () => ({
        url: '/getPageTexts',
        method: 'GET',
      }),
      providesTags: ['PageTexts'],
    }),
    getAllContactAttempts: builder.query<ContactAttempt[], string>({
      query: (id) => ({
        url: `?participantId=${id}`,
      }),
      transformResponse: (response: ContactAttempt[]) => {
        return response.map((item) => {
          return {
            ...item,
            contactTypeText:
              ContactAttemptTypeText[
                item.type as keyof typeof ContactAttemptTypeText
              ],
            answerText:
              ContactAttemptAnswerText[
                item.answer as keyof typeof ContactAttemptAnswerText
              ],
          } as ContactAttempt;
        });
      },
      providesTags: ['ContactAttempts'],
    }),
    AddContactAttempt: builder.mutation<
      AddContactAttemptResponse,
      {
        model: AddContactAttempt;
        participantId: string;
      }
    >({
      query: ({ model, participantId }) => ({
        url: `?participantId=${participantId}`,
        method: 'POST',
        body: model,
      }),
      invalidatesTags: ['ContactAttempts'],
    }),
    updateContactAttempt: builder.mutation<
      void,
      { contactAttempt: UpdateContactAttempt; id: string }
    >({
      query: ({ contactAttempt, id }) => ({
        url: `?participantId=${id}`,
        method: 'PUT',
        body: contactAttempt,
      }),
      invalidatesTags: ['ContactAttempts'],
    }),
    deleteContactAttempt: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ContactAttempts'],
    }),
    finishParticipant: builder.mutation<
      void,
      { type: PortalTypes; id: string }
    >({
      query: ({ type, id }) => ({
        url: `/finishParticipant?participantId=${id}&portalType=${type}`,
        method: 'PUT',
      }),
      invalidatesTags: ['ContactAttempts'],
    }),
  }),
});

export const {
  useLazyGetPageTextsQuery,
  useLazyGetAllContactAttemptsQuery,
  useAddContactAttemptMutation,
  useUpdateContactAttemptMutation,
  useDeleteContactAttemptMutation,
  useFinishParticipantMutation,
} = contactAttemptsApi;
